import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

// Local Storage anahtarı
const ANIKARE_CART_ID_KEY = 'anIkareCartId';

// --- Tipler ---
interface Product {
  _id: string;
  productId: string;
  name: string;
  price: number;
  type: 'package' | 'addon';
  category?: 'dugun' | 'nisan' | 'dogum-gunu' | 'other';
  features?: string[];
  freeTableCardQty?: number;
  freeHandCardQty?: number;
  imageUrl?: string;
  maxQty?: number;
  createdAt: string;
  updatedAt: string;
}

interface CartItem {
  product: Product;
  quantity: number;
}

interface Cart {
  _id: string;
  items: CartItem[];
  createdAt: string;
  updatedAt: string;
}

interface CartApiResponse {
  cart: Cart | null;
  cartId?: string;
  message?: string;
}

interface ICartContext {
  cart: Cart | null;
  loading: boolean;
  cartId: string | null;
  addToCart: (productId: string, quantity: number) => Promise<void>;
  removeFromCart: (productId: string) => Promise<void>;
}

interface CartProviderProps {
  children: ReactNode;
}

// --- Context Tanımı ---
const defaultState: ICartContext = {
  cart: null,
  loading: true,
  cartId: null,
  addToCart: async () => {},
  removeFromCart: async () => {},
};

const CartContext = createContext<ICartContext>(defaultState);

export const useCart = () => useContext(CartContext);

// --- Provider ---
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentCartId, setCurrentCartId] = useState<string | null>(null);

  // --- 1. Sepet Yükleme ---
  useEffect(() => {
    const fetchCart = async () => {
      const existingCartId = localStorage.getItem(ANIKARE_CART_ID_KEY);

      try {
        const res = await fetch(`/api/cart${existingCartId ? `?cartId=${existingCartId}` : ''}`, {
          credentials: 'include',
        });

        if (!res.ok) throw new Error(`Sepet alınamadı: ${res.status}`);

        const data: CartApiResponse = await res.json();

        if (data.cart) {
          setCart(data.cart);
          setCurrentCartId(data.cartId || existingCartId);
        } else {
          localStorage.removeItem(ANIKARE_CART_ID_KEY);
          setCurrentCartId(null);
          setCart(null);
        }
      } catch (error) {
        console.error('Sepet alınamadı:', error);
        setCart(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, []);

  // --- 2. Sepete Ekleme ---
  const addToCart = async (productId: string, quantity: number) => {
    setLoading(true);

    const updatedItems =
      cart?.items.map(item => ({
        productId: item.product.productId,
        quantity: item.quantity,
      })) || [];

    const existingItemIndex = updatedItems.findIndex(i => i.productId === productId);
    if (existingItemIndex > -1) {
      updatedItems[existingItemIndex].quantity = quantity;
    } else {
      updatedItems.push({ productId, quantity });
    }

    const existingCartId = currentCartId || localStorage.getItem(ANIKARE_CART_ID_KEY) || undefined;

    try {
      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cartId: existingCartId,
          items: updatedItems,
        }),
      });

      if (!res.ok) throw new Error(`Ürün eklenemedi: ${res.status}`);

      const data: CartApiResponse = await res.json();

      if (data.cartId) {
        localStorage.setItem(ANIKARE_CART_ID_KEY, data.cartId);
        setCurrentCartId(data.cartId);
      }

      if (data.cart) setCart(data.cart);
    } catch (error) {
      console.error('Ürün eklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- 3. Sepetten Silme ---
  const removeFromCart = async (productId: string) => {
    if (!currentCartId) {
      console.warn('Sepet ID\'si yok, cookie üzerinden backend sessionId kullanılıyor.');
    }

    try {
      const res = await fetch(
        `/api/cart/${currentCartId ? encodeURIComponent(currentCartId) + '/' : ''}${encodeURIComponent(productId)}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!res.ok) throw new Error(`Ürün silinemedi: ${res.status}`);

      const data: CartApiResponse = await res.json();
      if (data.cart) setCart(data.cart);
    } catch (error) {
      console.error('Ürün silinemedi:', error);
    }
  };

  // --- Context Değeri ---
  const value: ICartContext = {
    cart,
    loading,
    cartId: currentCartId,
    addToCart,
    removeFromCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
